<template>
  <nav class="nav">
		<nuxt-link :to="localePath('/office-art')" class="nav__header-link">Services</nuxt-link>
		<nuxt-link :to="localePath('/projects')" class="nav__header-link">Projects</nuxt-link>
		<nuxt-link :to="localePath('/contact')" class="nav__header-link">Contact</nuxt-link>
		<navigation-lang-switcher />
    <div class="nav-icon" :class="navOpen === true ? 'nav--active' : ''" @click="$toggleNav">
      <span></span>
      <span></span>
    </div>
    <div class="nav__panel" :class="navOpen === true ? 'nav--active' : ''">
      <div class="row">
        <div class="col-xs-12 col-md-4">
          <div class="nav__block nav-anim" ref="nav-anim">
            <h2 class="nav__heading">{{ mainHeading }}</h2>
            <navigation-links :content="mainLinks" class="nav__links--main"  />
          </div>
        </div>
        <div class="col-xs-12 col-md-8">
          <div class="nav__block nav-anim" ref="nav-anim">
            <h2 class="nav__heading">{{ aboutHeading }}</h2>
            <navigation-links :content="aboutLinks" class="nav__links--about"  />
          </div>
          <div class="nav__block nav-anim">
            <h2 class="nav__heading">{{ servicesHeading }}</h2>
            <navigation-links :content="servicesLinks" class="nav__links--services"  />
          </div>
        </div>
      </div>
      <div class="row nav__row--second desktop-only-flex">
	        <div v-if="navProject" class="col-xs-12 col-md-6 col-xl-4 col__featured nav-anim" ref="nav-anim">
	          <navigation-project :content="navProject" class="nav__project nav__feature" />
	        </div>
	        <div v-if="newsItem" class="col-xs-12 col-md-6 col-xl-4 col__featured nav-anim" ref="nav-anim">
	          <navigation-news-item :content="newsItem" class="nav__news-item nav__feature" />
	        </div>
      </div>
    </div>
  </nav>
</template>

<script setup>
	import { useI18n, useLocalePath } from '#imports'
	import { computed } from "vue"
	import { useState } from "nuxt/app"
	const { locale } = useI18n()
	const localePath = useLocalePath()
	const navOpen = useState('false')
	  const props = defineProps({
	    content: Object
	  })
	const navigation = computed(() => props.content?.navigation?.[0] ?? {})
	const mainLinks = computed(() => navigation.value.mainLinks ?? [])
	const aboutLinks = computed(() => navigation.value.aboutLinks ?? [])
	const servicesLinks = computed(() => navigation.value.servicesLinks ?? [])
	const navProject = computed(() => navigation.value.navProject ?? null)
	const newsItem = computed(() => props.content?.newsletter ?? null)
	const mainHeading = computed(() => locale.value === 'en'
		? navigation.value.mainLinksTitle?.en
		: navigation.value.mainLinksTitle?.jp
	)
	const aboutHeading = computed(() => locale.value === 'en'
		? navigation.value.aboutLinksTitle?.en
		: navigation.value.aboutLinksTitle?.jp
	)
	const servicesHeading = computed(() => locale.value === 'en'
		? navigation.value.servicesLinksTitle?.en
		: navigation.value.servicesLinksTitle?.jp
	)
</script>

<style lang="scss">
@use '@/assets/sass/variables.scss' as v;
.nav {
  align-items: center;
  background-color: v.$black;
  border-radius: 2rem;
  color: #fff;
  display: flex;
  font-size: 1rem;
  justify-content: space-between;
  margin: 0;
  padding: 0.5rem 1.5rem;
  transform-origin: right center;
  width: 15rem;
  @include v.respond-to('small') {
    align-items: center;
		font-size: v.$font-size-med;
    justify-content: space-between;
    margin: 1rem;
    padding: 0.305rem 0.5rem;
    width: 35rem;
  }
	@include v.respond-to('small') {
		width: 55rem;
	}
	@include v.respond-to('medium') {
		width: 35rem;
	}
  &__header-link {
    border-radius: 1.5rem;
    color: v.$white;
    display: none;
		font-family: var(--font);
    font-size: v.$font-size-med;
    justify-self: right;
    padding: 0.5rem;
    @include v.respond-to('small') {
      display: block;
    }
		@include v.respond-to('medium') {
			padding: 0.5rem 1rem;
		}
  }
}

.nav-icon {
  display: flex;
  align-items: center;
  cursor: pointer;
  justify-content: center;
  position: relative;
  right: 0;
  width: 1.75rem;
  height: 1.25rem;
  @include v.respond-to('small') {
    margin: 0 1rem;
  }

  span {
    position: absolute;
    display: block;
    width: 1.75rem;
    height: 0.2rem;
    background-color: v.$white;
    border-radius: 1rem;
    transition: all 0.2s cubic-bezier(0.1, 0.82, 0.76, 0.965);

    &:first-of-type {
      top: 0;
    }

    &:last-of-type {
      bottom: 0;
    }
  }

  &.nav--active {
    span {
      &:first-of-type {
        transform: rotate(45deg);
        top: 0.525rem;
      }

      &:last-of-type {
        transform: rotate(-45deg);
        bottom: 0.525rem;
      }
    }
  }

  &.nav--active:hover span:first-of-type,
  &.nav--active:hover span:last-of-type {
    background-color: v.$teal;
  }
}
.nav__bg {
  background-color: v.$white;
  opacity: 0;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  transition: 0.3s;
  visibility: hidden;
  &.nav--active {
    opacity: 0.75;
    transition: 0.3s;
    visibility: visible;
  }
}
.nav__panel {
  background-color: v.$black;
  border-radius: 2rem;
  left: 0.5rem;
  right: 0.5rem;
  opacity: 0;
  padding: 1.5rem;
  position: fixed;
  top: 5rem;
  transform: translateY(2rem);
  transform-origin: top center;
  transition-timing-function: ease-in;
  transition: all 0.3s;
  visibility: hidden;
  width: calc(100% - 1rem);
  z-index: 100;
  @include v.respond-to('small') {
    padding: 3rem;
    top: 6rem;
  }
  @include v.respond-to('medium') {
    left: 2rem;
    right: 2rem;
    width: calc(100% - 4rem);
  }
  .nav__block {
    display: block;
    padding: 0 0 1.5rem 0;
    position: relative;
    @include v.respond-to('medium') {
      padding: 0 0 2rem 0;
    }
    &:nth-child(2){
      padding: 0;
    }
  }
  .nav__heading {
    display: block;
    font-size: v.$font-size-lg;
    margin: 0 0 0.5rem 0;
    @include v.respond-to('medium') {
      font-size: v.$font-size-xl;
    }
  }
  .nav__links {
    padding: 0;
		font-family: var(--font);
    li {
      margin: 0 0.5rem 0 0;
      @include v.respond-to('medium') {
        margin: 0 2rem 0 0;
      }
    }
    &--main {
      li {
        display: inline-block;
				@include v.respond-to('small') {
					display: block;
				}
        a {
          display: inline-block;
					font-size: v.$font-size-med;
					font-weight: 300;
        }
      }
    }
    &--about, &--services {
      li {
        display: inline-block;
         a {
          font-size: v.$font-size;
        }
      }
    }
  }
  .featured {
    color: v.$white;
    display: none;
    height: 12rem;
    margin: 5rem 1rem 0 0;
		overflow: hidden;
    position: relative;
    @media screen and (min-height: 50rem) {
      display: flex;
    }
    &:hover {
      background: v.$teal;
    }
    &__image {
      height: 100%;
      object-fit: cover;
    }
    &__title, &__type, &__date {
			line-height: 2rem;
      margin: 1rem 0 0 1rem;
    }
		&__type {
			font-weight: 400;
		}
    &__content-type {
      font-size: v.$font-size-sm;
			font-weight: 400;
      letter-spacing: 0.2rem;
      margin: 0.5rem 0 0 1rem;
      text-transform: uppercase;
    }
  }
  .nav-anim {
    opacity: 0;
    transform: translateX(6rem);
  }

  &.nav--active {
    opacity: 1;
    transition: all 0.3s;
    visibility: visible;
    @include v.respond-to('medium') {
      width: calc(100% - 4rem);
    }
    .nav__block {
      transform: translateX(6rem);
      transition: 0.3s;
    }
  }
}
</style>
